import styles from "./bot.module.css";

import { Messages1 } from "iconsax-react";

import { ActionIcon, deepMerge, Dialog, MantineProvider } from "@mantine/core";
import { useState } from "react";

import { defaultConfiguration } from "@/libs/config";
import { View, type DefaultConfiguration, type ViewType } from "@/types";

import {
  FeedBackForm,
  LiveChat,
  Navigation,
  ReportAnIssueForm,
  Support,
} from "..";
import { SupportInfoWrapper } from "../faqs/info/wrapper";

export const FeedBackBot = (defaultProps: DefaultConfiguration) => {
  if (!defaultProps.apiKey || !defaultProps.apiURL) {
    throw new Error("API URL and API key not defined");
  }

  const props = deepMerge(defaultConfiguration, defaultProps);
  const { authStatus, sections, supportGuide } = props;

  const [dialogOpened, setDialogOpened] = useState(false);
  const [currentView, setCurrentView] = useState<ViewType>(View.Home);
  const [selectedId, setSelectedId] = useState<null | number>(null);

  const toggleDialog = () => setDialogOpened((prev) => !prev);

  const goToView = (view: ViewType, id?: number) => {
    setSelectedId(id ?? null);
    setCurrentView(view);
  };

  const viewMap = {
    [View.Chat]: <LiveChat />,
    [View.Feedback]: (
      <FeedBackForm
        apiKey={props.apiKey}
        apiURL={props.apiURL}
        authStatus={authStatus}
        onBack={() => setCurrentView(View.Home)}
        supportGuide={supportGuide}
      />
    ),
    [View.Home]: (
      <Support
        apiKey={props.apiKey}
        apiURL={props.apiURL}
        navigate={(view, id) => {
          if (view === "support-info" && id !== undefined) {
            goToView(View.Info, id);
          } else if (view === "feed-back-form") {
            goToView(View.Feedback);
          }
        }}
        supportGuide={supportGuide}
      />
    ),
    [View.Info]: (
      <SupportInfoWrapper
        id={selectedId}
        onBack={() => setCurrentView(View.Home)}
        supportGuide={supportGuide}
      />
    ),
    [View.Issue]: (
      <ReportAnIssueForm
        apiKey={props.apiKey}
        apiURL={props.apiURL}
        authStatus={authStatus}
        issueType={supportGuide?.system}
      />
    ),
  };

  const renderContent = () => viewMap[currentView] ?? null;

  return (
    <MantineProvider>
      <Dialog
        mb={40}
        onClose={toggleDialog}
        opened={dialogOpened}
        p={0}
        radius="md"
        style={{ maxWidth: 400, width: "100%" }}
      >
        <div
          className={styles.dialogContainer}
          style={{
            background: `linear-gradient(to bottom, ${supportGuide?.gradient?.[0]}, transparent 200px)`,
          }}
        >
          <div className={styles.dialogContent}>{renderContent()}</div>
          <Navigation sections={sections} navigate={goToView} />
        </div>
      </Dialog>

      <ActionIcon
        onClick={toggleDialog}
        radius={4}
        size={40}
        style={{
          backgroundColor: "#e2261c",
          bottom: "20px",
          position: "fixed",
          right: "20px",
          zIndex: 1000,
        }}
        variant="filled"
      >
        <Messages1 color="#FFFFFF" size={20} variant="Bold" />
      </ActionIcon>
    </MantineProvider>
  );
};
